import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../hooks/useAuth';
import { whiteLabelApi, accountsApi } from '../lib/api';
import { resizeImage, LOGO_BOUNDS, FAVICON_BOUNDS } from '../lib/imageResize';
import { Spinner, Alert } from '@rankhelm/ui';
import type { CustomHostnameInfo, CustomDomainStatus } from '@rankhelm/shared';

type Tab = 'branding' | 'domain';

export function AccountSettingsPage() {
  const { user, account, setAccount, isImpersonating } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState<Tab>('branding');

  // White-label state
  const [logoUrl, setLogoUrl] = useState('');
  const [faviconUrl, setFaviconUrl] = useState('');
  const [primaryColor, setPrimaryColor] = useState('#6366f1');
  const [secondaryColor, setSecondaryColor] = useState('#1e1b4b');
  const [accentColor, setAccentColor] = useState('#f59e0b');
  const [companyName, setCompanyName] = useState('');
  const [supportEmail, setSupportEmail] = useState('');
  const [customCss, setCustomCss] = useState('');

  // Branding uploads (Sprint 10.5G #5)
  const [logoUploading, setLogoUploading] = useState(false);
  const [faviconUploading, setFaviconUploading] = useState(false);
  const logoFileRef = useRef<HTMLInputElement>(null);
  const faviconFileRef = useRef<HTMLInputElement>(null);

  // Account settings — custom domain (Sprint 10.5G Group 5)
  const [customDomain, setCustomDomain] = useState('');
  const [domainStatus, setDomainStatus] = useState<CustomDomainStatus | null>(null);
  const [hostnameInfo, setHostnameInfo] = useState<CustomHostnameInfo | null>(null);
  const [accountSaving, setAccountSaving] = useState(false);
  const [accountMessage, setAccountMessage] = useState('');
  const [accountError, setAccountError] = useState('');
  const [refreshing, setRefreshing] = useState(false);

  // Super admin (not impersonating) does not manage a specific account's
  // white-label settings here — those belong to the impersonated account.
  const isSuperAdminOnly = user?.role === 'super_admin' && !isImpersonating;

  useEffect(() => {
    if (account?.id && !isSuperAdminOnly) loadData();
    else setLoading(false);
  }, [account?.id, isSuperAdminOnly]);

  // Seed the domain form + status badge from the auth context snapshot.
  useEffect(() => {
    setCustomDomain(account?.custom_domain ?? '');
    setDomainStatus(account?.custom_domain_status ?? null);
  }, [account?.custom_domain, account?.custom_domain_status]);

  async function loadData() {
    setLoading(true);
    try {
      const res = await whiteLabelApi.get(account!.id);
      if (res.data) {
        setLogoUrl(res.data.logo_url || '');
        setFaviconUrl(res.data.favicon_url || '');
        setPrimaryColor(res.data.primary_color || '#6366f1');
        setSecondaryColor(res.data.secondary_color || '#1e1b4b');
        setAccentColor(res.data.accent_color || '#f59e0b');
        setCompanyName(res.data.company_name || '');
        setSupportEmail(res.data.support_email || '');
        setCustomCss(res.data.custom_css || '');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load white-label config');
    } finally {
      setLoading(false);
    }
  }

  async function handleSaveWhiteLabel(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setError('');
    setMessage('');
    try {
      await whiteLabelApi.upsert(account!.id, {
        logo_url: logoUrl || null,
        favicon_url: faviconUrl || null,
        primary_color: primaryColor,
        secondary_color: secondaryColor,
        accent_color: accentColor,
        company_name: companyName || null,
        support_email: supportEmail || null,
        custom_css: customCss || null,
      });
      setMessage('White-label settings saved successfully.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save white-label settings');
    } finally {
      setSaving(false);
    }
  }

  async function handleSaveDomain(e: React.FormEvent) {
    e.preventDefault();
    setAccountSaving(true);
    setAccountError('');
    setAccountMessage('');
    try {
      const res = await accountsApi.update(account!.id, {
        custom_domain: customDomain || null,
      });
      setDomainStatus(res.data.custom_domain_status ?? null);
      setHostnameInfo(res.hostname ?? null);
      // Sync the updated account back into the auth context so the Refresh
      // button (gated on account?.custom_domain_cf_id) appears immediately
      // after a successful save, and the status badge stays current.
      if (account) {
        setAccount({
          ...account,
          custom_domain: res.data.custom_domain ?? null,
          custom_domain_cf_id: res.data.custom_domain_cf_id ?? null,
          custom_domain_status: res.data.custom_domain_status ?? null,
        });
      }
      if (res.cf_error) {
        // CF call failed but DB write succeeded; show the underlying message
        // so the admin can self-serve (e.g. hostname already claimed).
        setAccountError(`Cloudflare: ${res.cf_error}`);
      } else if (res.hostname) {
        setAccountMessage(
          'Domain registered with Cloudflare. Add the DNS records below — SSL will auto-activate once DNS + ownership validate.',
        );
      } else {
        setAccountMessage('Custom domain cleared.');
      }
    } catch (err) {
      setAccountError(err instanceof Error ? err.message : 'Failed to update custom domain');
    } finally {
      setAccountSaving(false);
    }
  }

  async function handleRefreshDomain() {
    if (!account?.id) return;
    setRefreshing(true);
    setAccountError('');
    try {
      const res = await accountsApi.refreshCustomDomain(account.id);
      setDomainStatus(res.data.custom_domain_status ?? null);
      setHostnameInfo(res.hostname ?? null);
      if (account) {
        setAccount({
          ...account,
          custom_domain_status: res.data.custom_domain_status ?? null,
        });
      }
      if (res.hostname?.status === 'active') {
        setAccountMessage('Custom domain is live with a valid TLS certificate.');
      } else {
        setAccountMessage(
          'Status refreshed. If DNS + ownership are still pending, give it a few minutes and try again.',
        );
      }
    } catch (err) {
      setAccountError(err instanceof Error ? err.message : 'Failed to refresh domain status');
    } finally {
      setRefreshing(false);
    }
  }

  async function handleBrandingUpload(
    e: React.ChangeEvent<HTMLInputElement>,
    kind: 'logo' | 'favicon',
  ) {
    const input = e.target;
    const file = input.files?.[0];
    if (!file || !account?.id) return;

    const setUploading = kind === 'logo' ? setLogoUploading : setFaviconUploading;
    setError('');
    setMessage('');
    setUploading(true);
    try {
      // Browsers don't have a native image resizer, so we downscale on the
      // canvas before upload. SVG/ICO pass through unchanged (see
      // `resizeImage` — ADR-020).
      const bounds = kind === 'logo' ? LOGO_BOUNDS : FAVICON_BOUNDS;
      const resized = await resizeImage(file, bounds);
      const res = kind === 'logo'
        ? await whiteLabelApi.uploadLogo(account.id, resized)
        : await whiteLabelApi.uploadFavicon(account.id, resized);
      if (kind === 'logo') setLogoUrl(res.url);
      else setFaviconUrl(res.url);
      setMessage(`${kind === 'logo' ? 'Logo' : 'Favicon'} uploaded.`);
    } catch (err) {
      setError(err instanceof Error ? err.message : `Failed to upload ${kind}`);
    } finally {
      setUploading(false);
      input.value = '';
    }
  }

  const isAdmin = user?.role === 'super_admin' || user?.role === 'account_admin';

  if (!isAdmin) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-slate-400">You don't have permission to view account settings.</div>
      </div>
    );
  }

  if (loading) {
    return <Spinner message="Loading settings..." fullPage />;
  }

  // Super admin (not impersonating) shouldn't see agency white-label controls;
  // those settings belong to individual accounts.
  if (isSuperAdminOnly) {
    return (
      <div>
        <h1 className="text-2xl font-bold text-white mb-6">Account Settings</h1>
        <div className="bg-navy-900 border border-navy-700 rounded-xl p-6 max-w-2xl">
          <h2 className="text-lg font-semibold text-white mb-2">Super admin</h2>
          <p className="text-sm text-slate-400">
            White-label branding and custom domains are configured per account. To
            manage settings for a specific agency or individual, open that
            account from{' '}
            <a href="/accounts" className="text-helm-400 hover:text-helm-300">
              Accounts
            </a>{' '}
            and impersonate an admin there.
          </p>
        </div>
      </div>
    );
  }

  const tabButton = (tab: Tab, label: string) => (
    <button
      type="button"
      onClick={() => setActiveTab(tab)}
      className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
        activeTab === tab
          ? 'border-helm-500 text-white'
          : 'border-transparent text-slate-400 hover:text-slate-200'
      }`}
    >
      {label}
    </button>
  );

  return (
    <div>
      <h1 className="text-2xl font-bold text-white mb-6">Account Settings</h1>

      <div className="border-b border-navy-700 mb-6 flex gap-2 max-w-2xl">
        {tabButton('branding', 'Branding')}
        {tabButton('domain', 'Custom Domain')}
      </div>

      <div className="space-y-8 max-w-2xl">
        {activeTab === 'domain' && (
          <form onSubmit={handleSaveDomain} className="bg-navy-900 border border-navy-700 rounded-xl p-6 space-y-5">
            <div className="flex items-center justify-between gap-3">
              <h2 className="text-lg font-semibold text-white">Custom Domain</h2>
              <DomainStatusBadge status={domainStatus} />
            </div>
            <p className="text-sm text-slate-400">
              Point a CNAME record from your domain to{' '}
              <code className="text-helm-400">proxy.rankhelm.com</code>, then
              click Save. We'll register your hostname with Cloudflare and
              provision a TLS certificate automatically — usually a few
              minutes once DNS propagates.
            </p>

            {accountError && <Alert variant="error">{accountError}</Alert>}
            {accountMessage && <Alert variant="success">{accountMessage}</Alert>}

            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1.5">Domain</label>
              <input
                type="text"
                value={customDomain}
                onChange={(e) => setCustomDomain(e.target.value)}
                placeholder="app.yourdomain.com"
                className="w-full px-4 py-2.5 bg-navy-800 border border-navy-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-helm-500"
              />
            </div>

            <div className="flex items-center gap-3">
              <button
                type="submit"
                disabled={accountSaving}
                className="px-6 py-2.5 bg-helm-500 hover:bg-helm-400 text-white font-medium rounded-lg transition-colors disabled:opacity-50"
              >
                {accountSaving ? 'Saving...' : 'Save Domain'}
              </button>
              {account?.custom_domain_cf_id && (
                <button
                  type="button"
                  onClick={handleRefreshDomain}
                  disabled={refreshing}
                  className="px-4 py-2.5 text-sm text-slate-300 hover:text-white border border-navy-600 hover:border-navy-500 rounded-lg transition-colors disabled:opacity-50"
                >
                  {refreshing ? 'Refreshing…' : 'Refresh status'}
                </button>
              )}
            </div>

            {hostnameInfo && <CustomDomainInstructions info={hostnameInfo} />}
          </form>
        )}

        {activeTab === 'branding' && (
          <form onSubmit={handleSaveWhiteLabel} className="bg-navy-900 border border-navy-700 rounded-xl p-6 space-y-5">
            <h2 className="text-lg font-semibold text-white">Branding</h2>
            <p className="text-sm text-slate-400">
              Customize the look and feel of your portal for your clients.
            </p>

            {error && <Alert variant="error">{error}</Alert>}
            {message && <Alert variant="success">{message}</Alert>}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1.5">Company Name</label>
                <input
                  type="text"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  placeholder="Your Agency Name"
                  className="w-full px-4 py-2.5 bg-navy-800 border border-navy-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-helm-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1.5">Support Email</label>
                <input
                  type="email"
                  value={supportEmail}
                  onChange={(e) => setSupportEmail(e.target.value)}
                  placeholder="support@youragency.com"
                  className="w-full px-4 py-2.5 bg-navy-800 border border-navy-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-helm-500"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1.5">Logo</label>
                <div className="flex items-center gap-4">
                  <div className="w-28 h-14 bg-navy-800 border border-navy-600 rounded-lg flex items-center justify-center overflow-hidden flex-shrink-0">
                    {logoUrl ? (
                      <img src={logoUrl} alt="Logo" className="max-w-full max-h-full object-contain" />
                    ) : (
                      <span className="text-xs text-slate-500">No logo</span>
                    )}
                  </div>
                  <div className="min-w-0">
                    <button
                      type="button"
                      onClick={() => logoFileRef.current?.click()}
                      disabled={logoUploading}
                      className="text-sm text-helm-400 hover:text-helm-300 font-medium transition-colors disabled:opacity-50"
                    >
                      {logoUploading ? 'Uploading…' : logoUrl ? 'Replace logo' : 'Upload logo'}
                    </button>
                    <p className="text-xs text-slate-500 mt-0.5">PNG, JPG, WebP, or SVG. Resized to fit 400×100. Max 2MB.</p>
                    <input
                      ref={logoFileRef}
                      type="file"
                      accept="image/png,image/jpeg,image/webp,image/svg+xml"
                      className="hidden"
                      onChange={(e) => handleBrandingUpload(e, 'logo')}
                    />
                  </div>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1.5">Favicon</label>
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-navy-800 border border-navy-600 rounded-lg flex items-center justify-center overflow-hidden flex-shrink-0">
                    {faviconUrl ? (
                      <img src={faviconUrl} alt="Favicon" className="max-w-full max-h-full object-contain" />
                    ) : (
                      <span className="text-xs text-slate-500">None</span>
                    )}
                  </div>
                  <div className="min-w-0">
                    <button
                      type="button"
                      onClick={() => faviconFileRef.current?.click()}
                      disabled={faviconUploading}
                      className="text-sm text-helm-400 hover:text-helm-300 font-medium transition-colors disabled:opacity-50"
                    >
                      {faviconUploading ? 'Uploading…' : faviconUrl ? 'Replace favicon' : 'Upload favicon'}
                    </button>
                    <p className="text-xs text-slate-500 mt-0.5">PNG or ICO. Resized to 128×128. Max 256KB.</p>
                    <input
                      ref={faviconFileRef}
                      type="file"
                      accept="image/png,image/x-icon,image/vnd.microsoft.icon"
                      className="hidden"
                      onChange={(e) => handleBrandingUpload(e, 'favicon')}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1.5">Primary Color</label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={primaryColor}
                    onChange={(e) => setPrimaryColor(e.target.value)}
                    aria-label="Primary color"
                    className="rh-color-swatch w-10 h-10 rounded border border-navy-600 cursor-pointer shrink-0"
                  />
                  <input
                    type="text"
                    value={primaryColor}
                    onChange={(e) => setPrimaryColor(e.target.value)}
                    className="w-28 px-3 py-2 bg-navy-800 border border-navy-600 rounded-lg text-white text-sm font-mono focus:outline-none focus:ring-2 focus:ring-helm-500"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1.5">Secondary Color</label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={secondaryColor}
                    onChange={(e) => setSecondaryColor(e.target.value)}
                    aria-label="Secondary color"
                    className="rh-color-swatch w-10 h-10 rounded border border-navy-600 cursor-pointer shrink-0"
                  />
                  <input
                    type="text"
                    value={secondaryColor}
                    onChange={(e) => setSecondaryColor(e.target.value)}
                    className="w-28 px-3 py-2 bg-navy-800 border border-navy-600 rounded-lg text-white text-sm font-mono focus:outline-none focus:ring-2 focus:ring-helm-500"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1.5">Accent Color</label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={accentColor}
                    onChange={(e) => setAccentColor(e.target.value)}
                    aria-label="Accent color"
                    className="rh-color-swatch w-10 h-10 rounded border border-navy-600 cursor-pointer shrink-0"
                  />
                  <input
                    type="text"
                    value={accentColor}
                    onChange={(e) => setAccentColor(e.target.value)}
                    className="w-28 px-3 py-2 bg-navy-800 border border-navy-600 rounded-lg text-white text-sm font-mono focus:outline-none focus:ring-2 focus:ring-helm-500"
                  />
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1.5">Preview</label>
              <div className="p-4 rounded-lg border border-navy-600" style={{ backgroundColor: secondaryColor }}>
                <div className="flex items-center gap-3 mb-3">
                  {logoUrl && <img src={logoUrl} alt="Logo" className="h-8 w-auto" onError={(e) => (e.currentTarget.style.display = 'none')} />}
                  <span className="font-semibold" style={{ color: primaryColor }}>
                    {companyName || 'Your Agency'}
                  </span>
                </div>
                <div className="flex gap-2">
                  <span className="px-3 py-1 rounded text-xs text-white font-medium" style={{ backgroundColor: primaryColor }}>
                    Primary
                  </span>
                  <span className="px-3 py-1 rounded text-xs text-white font-medium" style={{ backgroundColor: accentColor }}>
                    Accent
                  </span>
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1.5">Custom CSS (advanced)</label>
              <textarea
                value={customCss}
                onChange={(e) => setCustomCss(e.target.value)}
                rows={4}
                placeholder="/* Custom CSS overrides */"
                className="w-full px-4 py-2.5 bg-navy-800 border border-navy-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-helm-500 font-mono text-sm"
              />
            </div>

            <button
              type="submit"
              disabled={saving}
              className="px-6 py-2.5 bg-helm-500 hover:bg-helm-400 text-white font-medium rounded-lg transition-colors disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Save Branding'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

/**
 * Four-state pill matching the collapsed CF status (see
 * `deriveStatus` in the API). Rendered only when a custom domain is set;
 * null maps to no badge so freshly-loaded accounts don't flash a state.
 */
function DomainStatusBadge({ status }: { status: CustomDomainStatus | null }) {
  if (!status) return null;
  const config: Record<CustomDomainStatus, { label: string; className: string }> = {
    pending: {
      label: 'Pending DNS',
      className: 'bg-amber-500/10 text-amber-300 border-amber-500/30',
    },
    pending_ssl: {
      label: 'Issuing certificate',
      className: 'bg-sky-500/10 text-sky-300 border-sky-500/30',
    },
    active: {
      label: 'Active',
      className: 'bg-emerald-500/10 text-emerald-300 border-emerald-500/30',
    },
    failed: {
      label: 'Failed',
      className: 'bg-rose-500/10 text-rose-300 border-rose-500/30',
    },
  };
  const { label, className } = config[status];
  return (
    <span className={`text-xs font-medium px-2.5 py-1 rounded-full border ${className}`}>
      {label}
    </span>
  );
}

/**
 * Renders the DNS records Cloudflare wants the agency to add so ownership
 * + SSL can validate. HTTP-validation cases only need the CNAME (already
 * in place); TXT-validation surfaces the TXT name/value verbatim.
 */
function CustomDomainInstructions({ info }: { info: CustomHostnameInfo }) {
  const sslRecords = info.ssl?.validation_records ?? [];
  const ownership = info.ownership_verification;
  const errors = info.ssl?.validation_errors?.filter((e) => e.message) ?? [];

  if (info.status === 'active') {
    return (
      <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-4 text-sm text-emerald-200">
        <p className="font-medium">Your custom domain is live.</p>
        <p className="mt-1 text-emerald-300/80">
          Traffic for <code>{info.hostname}</code> is now served with a valid TLS certificate.
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-navy-700 bg-navy-950 p-4 space-y-4 text-sm">
      <p className="text-slate-300">
        <span className="font-medium text-white">Next steps for {info.hostname}:</span>{' '}
        add the records below in your DNS provider. Once they propagate, Cloudflare
        will finish ownership validation and issue a TLS certificate.
      </p>

      {ownership?.name && ownership?.value && (
        <div>
          <div className="text-xs uppercase tracking-wide text-slate-500 mb-1">
            Ownership verification (TXT)
          </div>
          <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-slate-300">
            <dt className="text-slate-500">Name</dt>
            <dd className="font-mono break-all">{ownership.name}</dd>
            <dt className="text-slate-500">Value</dt>
            <dd className="font-mono break-all">{ownership.value}</dd>
          </dl>
        </div>
      )}

      {sslRecords.map((rec, i) => (
        <div key={i}>
          <div className="text-xs uppercase tracking-wide text-slate-500 mb-1">
            SSL validation {rec.status ? `(${rec.status})` : ''}
          </div>
          <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-slate-300">
            {rec.txt_name && (
              <>
                <dt className="text-slate-500">TXT name</dt>
                <dd className="font-mono break-all">{rec.txt_name}</dd>
              </>
            )}
            {rec.txt_value && (
              <>
                <dt className="text-slate-500">TXT value</dt>
                <dd className="font-mono break-all">{rec.txt_value}</dd>
              </>
            )}
            {rec.http_url && (
              <>
                <dt className="text-slate-500">HTTP URL</dt>
                <dd className="font-mono break-all">{rec.http_url}</dd>
              </>
            )}
            {rec.http_body && (
              <>
                <dt className="text-slate-500">HTTP body</dt>
                <dd className="font-mono break-all">{rec.http_body}</dd>
              </>
            )}
          </dl>
        </div>
      ))}

      {errors.length > 0 && (
        <div className="rounded border border-rose-500/30 bg-rose-500/5 p-3 text-rose-200">
          <div className="font-medium">Validation errors</div>
          <ul className="list-disc list-inside mt-1 space-y-0.5">
            {errors.map((e, i) => (
              <li key={i}>{e.message}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
